import React, { useEffect, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import { FD210, SourceReceipt, pwd210Api } from '../../../services/pwd210Api';
import { extractApiErrorMessage } from '../../../utils/errorHandler';

interface Props {
  onSelect: (draft: FD210) => void;
  onCancel: () => void;
}

export default function Pwd210SourcePicker({ onSelect, onCancel }: Props) {
  const [sourceType, setSourceType] = useState(1);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [rows, setRows] = useState<SourceReceipt[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const requestId = useRef(0);
  useEffect(() => () => { requestId.current += 1; }, []);

  const query = async (nextPage = 1) => {
    const request = ++requestId.current;
    setBusy(true);
    try {
      const result = await pwd210Api.sourceReceipts(sourceType, start, end, nextPage);
      if (request !== requestId.current) return;
      setRows(result.data); setTotal(result.total); setPage(nextPage);
    } catch (err) {
      if (request !== requestId.current) return;
      setRows([]); setTotal(0);
      message.error(extractApiErrorMessage(err, '來源查詢失敗'));
    } finally {
      if (request === requestId.current) setBusy(false);
    }
  };
  const select = async (row: SourceReceipt) => {
    const request = ++requestId.current;
    setBusy(true);
    try {
      const draft = await pwd210Api.sourceDraft(row.source_type, row.doc_no);
      if (request === requestId.current) onSelect(draft);
    } catch (err) {
      if (request === requestId.current) {
        const errors = (err as { response?: { data?: { errors?: { message: string }[] } } }).response?.data?.errors;
        message.error(errors?.[0]?.message || extractApiErrorMessage(err, '來源帶入失敗'));
      }
    } finally {
      if (request === requestId.current) setBusy(false);
    }
  };
  const clearResults = () => {
    requestId.current += 1; setRows([]); setTotal(0); setPage(1);
  };

  return (
    <Modal title="選擇來源入庫單" open onCancel={onCancel} footer={null} width={780}>
      <p>選擇繳庫單或託工入庫單，查詢後帶入單號及入庫明細。</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <label>來源類型
          <select aria-label="來源類型" value={sourceType} disabled={busy}
            onChange={e => { setSourceType(Number(e.target.value)); clearResults(); }}>
            <option value={1}>繳庫單</option><option value={2}>託工入庫單</option>
          </select>
        </label>
        <label>起始單號<input aria-label="起始單號" maxLength={8} value={start} disabled={busy}
          onChange={e => { setStart(e.target.value); clearResults(); }} /></label>
        <label>截止單號<input aria-label="截止單號" maxLength={8} value={end} disabled={busy}
          onChange={e => { setEnd(e.target.value); clearResults(); }} /></label>
        <button type="button" disabled={busy} onClick={() => query()}
          data-acceptance-action="PWD210F1-ACT-SOURCE-QUERY">查詢來源</button>
      </div>
      <table style={{ width: '100%' }} aria-label="來源入庫單">
        <thead><tr><th>單號</th><th>入庫日期</th><th>廠商</th><th>操作</th></tr></thead>
        <tbody>{rows.map(row => <tr key={row.doc_no}>
          <td>{row.doc_no}</td><td>{row.receipt_date?.substring(0, 10)}</td><td>{row.vendor}</td>
          <td><button type="button" disabled={busy} onClick={() => select(row)}
            data-acceptance-action="PWD210F1-ACT-SOURCE-SELECT"
            aria-label={'帶入 ' + row.doc_no}>帶入</button></td>
        </tr>)}</tbody>
      </table>
      {rows.length === 0 && <p role="status">{busy ? '載入中…' : '請查詢可新增驗布指示單的來源單據。'}</p>}
      <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
        <button type="button" disabled={busy || page <= 1} onClick={() => query(page - 1)}>上一頁</button>
        <span>第 {page} 頁，共 {total} 筆</span>
        <button type="button" disabled={busy || page * 20 >= total} onClick={() => query(page + 1)}>下一頁</button>
      </div>
    </Modal>
  );
}
